import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/api';
import { setAuthToken, setUser, removeAuthToken, getLastPortal, setLastPortal } from '@/lib/auth';
import useAppStore from '@/stores/appStore';
import { useNavigate } from 'react-router-dom';

const OPS_ROLES = ['agent', 'foc', 'technician', 'super_admin'];

const handleLoginSuccess = (response, queryClient, setCurrentUser, navigate) => {
  const { token, user } = response.data;
  setAuthToken(token);
  setUser(user);
  setCurrentUser(user);
  // Persist which portal this user belongs to so redirects work after refresh/logout
  setLastPortal(OPS_ROLES.includes(user.role) ? 'ops' : 'customer');
  queryClient.invalidateQueries({ queryKey: ['currentUser'] });
  
  const roleRoutes = {
    customer: '/customer',
    agent: '/ops/agent',
    foc: '/ops/foc',
    technician: '/ops/tech',
    super_admin: '/ops/admin',
  };
  navigate(roleRoutes[user.role] || '/');
};

export const useCurrentUser = () => {
  const setCurrentUser = useAppStore((state) => state.setCurrentUser);
  
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data } = await authApi.getCurrentUser();
      setUser(data);
      setCurrentUser(data);
      return data;
    },
    enabled: !!localStorage.getItem('auth_token'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useRequestOtp = () => {
  return useMutation({
    mutationFn: ({ phone, portal }) => authApi.requestOtp(phone, portal),
  });
};

export const useVerifyOtp = () => {
  const queryClient = useQueryClient();
  const setCurrentUser = useAppStore((state) => state.setCurrentUser);
  const navigate = useNavigate();
  
  return useMutation({
    mutationFn: ({ phone, otp, portal }) => authApi.verifyOtp(phone, otp, portal),
    onSuccess: (response) => {
      if (response?.data?.registration_required) {
        // Skip session redirection; component will handle user registration completion
        return;
      }
      handleLoginSuccess(response, queryClient, setCurrentUser, navigate);
    },
  });
};

export const useRegisterComplete = () => {
  const queryClient = useQueryClient();
  const setCurrentUser = useAppStore((state) => state.setCurrentUser);
  const navigate = useNavigate();
  
  return useMutation({
    mutationFn: (data) => authApi.registerComplete(data),
    onSuccess: (response) => {
      handleLoginSuccess(response, queryClient, setCurrentUser, navigate);
    },
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  const setCurrentUser = useAppStore((state) => state.setCurrentUser);
  const navigate = useNavigate();
  
  return useMutation({
    mutationFn: (data) => authApi.login(data),
    onSuccess: (response) => {
      handleLoginSuccess(response, queryClient, setCurrentUser, navigate);
    },
  });
};

export const useGoogleLogin = () => {
  const queryClient = useQueryClient();
  const setCurrentUser = useAppStore((state) => state.setCurrentUser);
  const navigate = useNavigate();
  
  return useMutation({
    mutationFn: ({ idToken, portal }) => authApi.googleLogin(idToken, portal),
    onSuccess: (response) => {
      handleLoginSuccess(response, queryClient, setCurrentUser, navigate);
    },
  });
};

export const useRegister = () => {
  return useMutation({
    mutationFn: (data) => authApi.register(data),
  });
};

export const useVerifyRegistration = () => {
  const queryClient = useQueryClient();
  const setCurrentUser = useAppStore((state) => state.setCurrentUser);
  const navigate = useNavigate();
  
  return useMutation({
    mutationFn: ({ phone, otp }) => authApi.verifyRegistration(phone, otp),
    onSuccess: (response) => {
      handleLoginSuccess(response, queryClient, setCurrentUser, navigate);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setCurrentUser = useAppStore((state) => state.setCurrentUser);
  
  return () => {
    const portal = getLastPortal();
    removeAuthToken();
    queryClient.clear();
    setCurrentUser(null);
    navigate(portal === 'ops' ? '/ops/login' : '/login');
  };
};